import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { ArrowLeft, CheckCircle, XCircle, Warning, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import api, { API_BASE } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import CpdSeal from "@/components/CpdSeal";

const LICENSE_LABELS = {
  CABIN_CREW: "Cabin Crew Attendant",
  CABIN_SENIOR: "Senior Cabin Crew — Purser",
  CABIN_SAFETY: "Cabin Safety Specialist",
  SAFETY_OFFICER: "Aviation Safety Officer",
  EMERGENCY_RESPONSE: "Emergency Response Specialist",
  FIRE_SAFETY: "Fire Safety & Rescue",
  GROUND_STAFF: "Ground Handling Agent",
  RAMP_AGENT: "Ramp / Apron Agent",
  LOAD_CONTROL: "Load Control Officer",
  DG_HANDLING: "Dangerous Goods Specialist",
  PASSENGER_SERVICE: "Passenger Service Agent",
};

const formatDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).toUpperCase();
  } catch {
    return iso;
  }
};

export default function VerificationResult() {
  const { certNumber } = useParams();
  const [state, setState] = useState({ loading: true, found: false, cert: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/verify/${certNumber}`);
        if (!cancelled) {
          setState({ loading: false, found: data.found, cert: data.certificate || null });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, found: false, cert: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [certNumber]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center label-eyebrow">Querying registry…</div>
      </div>
    );
  }

  const cert = state.cert;
  const found = state.found;
  const status = cert?.status || "INVALID";

  let banner;
  if (!found) {
    banner = {
      tone: "invalid",
      Icon: XCircle,
      title: "Not Found",
      msg: "No credential exists for this number in the IAVA registry.",
    };
  } else if (status === "ACTIVE") {
    banner = {
      tone: "valid",
      Icon: CheckCircle,
      title: "Valid Credential",
      msg: "This credential is active and matches the registry of record.",
    };
  } else if (status === "EXPIRED") {
    banner = {
      tone: "expired",
      Icon: Warning,
      title: "Expired Credential",
      msg: "This credential was issued by IAVA but has passed its expiry date.",
    };
  } else {
    banner = {
      tone: "invalid",
      Icon: XCircle,
      title: "Revoked",
      msg: "This credential has been revoked by the issuing authority.",
    };
  }

  const toneStyles = {
    valid: "bg-emerald-50 border-emerald-200 text-emerald-900",
    invalid: "bg-red-50 border-red-200 text-red-900",
    expired: "bg-amber-50 border-amber-200 text-amber-900",
  };
  const accentStyles = {
    valid: "bg-emerald-500",
    invalid: "bg-red-500",
    expired: "bg-amber-500",
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <PublicHeader />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 lg:pt-16 pb-24">
          <Link
            to="/"
            data-testid="back-to-verify-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft size={14} /> Verify another credential
          </Link>

          <div
            data-testid="cert-status-banner"
            className={`mt-8 border ${toneStyles[banner.tone]} relative overflow-hidden`}
          >
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentStyles[banner.tone]}`} />
            <div className="p-6 lg:p-8 flex items-start gap-5">
              <banner.Icon size={36} weight="fill" />
              <div className="flex-1">
                <div className="font-display text-2xl lg:text-3xl font-black tracking-tighter">
                  {banner.title}
                </div>
                <p className="mt-2 text-sm lg:text-base leading-relaxed opacity-90">{banner.msg}</p>
                <div className="mt-4 font-mono-plex text-xs opacity-80">
                  Certificate №&nbsp;
                  <span data-testid="cert-banner-number" className="font-bold">{certNumber}</span>
                </div>
              </div>
            </div>
          </div>

          {found && cert && (
            <div className="mt-10 bg-white border border-slate-300 shadow-[0_12px_40px_rgba(11,32,70,0.08)] relative cert-noise">
              <div className="bg-[#0B2046] text-white px-8 lg:px-12 py-8 flex items-center justify-between">
                <div>
                  <div className="font-display font-black tracking-tighter text-2xl">IAVA</div>
                  <div className="label-eyebrow text-slate-300 mt-1">
                    International Aviation Verification
                  </div>
                </div>
                <div className="text-right">
                  <div className="label-eyebrow text-slate-300">Certificate of competency</div>
                  <div className="font-mono-plex font-semibold mt-1" data-testid="cert-number-value">
                    {cert.certificate_number}
                  </div>
                </div>
              </div>

              <div className="px-8 lg:px-12 py-10 grid lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                  <div>
                    <div className="label-eyebrow">This is to certify that</div>
                    <div
                      data-testid="cert-holder-name"
                      className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3"
                    >
                      {cert.holder_name}
                    </div>
                    <div className="h-px w-32 bg-amber-600 mt-3" />
                  </div>

                  <div>
                    <div className="label-eyebrow">Has been granted the credential</div>
                    <div className="font-display text-2xl font-bold text-[#0B2046] mt-2">
                      {LICENSE_LABELS[cert.license_type] || cert.license_type}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-6 border-t border-slate-200">
                    <Field label="Nationality" value={cert.nationality} />
                    <Field label="Date of birth" value={formatDate(cert.date_of_birth)} mono />
                    <Field label="License code" value={cert.license_type} mono />
                    <Field label="Issue date" value={formatDate(cert.issue_date)} mono />
                    <Field label="Expiry date" value={formatDate(cert.expiry_date)} mono />
                    <Field label="Status" value={status} />
                  </div>

                  <div>
                    <div className="label-eyebrow">Issuing authority</div>
                    <div className="text-sm text-slate-800 mt-2">{cert.issuing_authority}</div>
                  </div>
                </div>

                <div className="lg:col-span-1 flex flex-col items-center lg:items-end justify-between gap-6">
                  <div className="bg-white border border-slate-200 p-4 flex flex-col items-center">
                    <QRCode
                      value={cert.verify_url}
                      size={140}
                      bgColor="#FFFFFF"
                      fgColor="#0B2046"
                      data-testid="cert-qr-code"
                    />
                    <div className="label-eyebrow mt-3">Scan to verify</div>
                  </div>

                  <div className="flex flex-col items-center" data-testid="cert-cpd-block">
                    <CpdSeal size={130} />
                    <div className="label-eyebrow mt-2 text-center">Accredited CPD Provider</div>
                    <div className="text-[10px] text-slate-500 font-mono-plex tracking-wider text-center mt-0.5">
                      Continuing Professional Development
                    </div>
                  </div>

                  <a
                    data-testid="download-pdf-button"
                    href={`${API_BASE}/certificates/${cert.certificate_number}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors font-medium text-sm w-full justify-center"
                  >
                    <DownloadSimple size={16} weight="bold" /> Download PDF
                  </a>
                </div>
              </div>

              <div className="px-8 lg:px-12 py-5 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 font-mono-plex">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={14} /> Verified against IAVA Master Registry
                </span>
                <span>Issued {formatDate(cert.created_at?.slice(0, 10))}</span>
              </div>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1.5 text-slate-900 ${mono ? "font-mono-plex font-medium" : "font-semibold"}`}>
        {value}
      </div>
    </div>
  );
}
